import { logout, useAppDispatch } from "@template/state";
import { ROUTES } from "@template/types";
import { Header, Sidebar, useThemeMode, type SidebarItem } from "@template/ui";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import SummarizeIcon from "@mui/icons-material/SummarizeOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrowsOutlined";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import TimelineIcon from "@mui/icons-material/TimelineOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUpOutlined";
import WavesIcon from "@mui/icons-material/WavesOutlined";
import FunctionsIcon from "@mui/icons-material/FunctionsOutlined";
import ApartmentIcon from "@mui/icons-material/ApartmentOutlined";
import GridViewIcon from "@mui/icons-material/GridViewOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import LockClockIcon from "@mui/icons-material/LockClockOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import ChecklistIcon from "@mui/icons-material/ChecklistOutlined";
import GroupIcon from "@mui/icons-material/GroupOutlined";
import ShieldIcon from "@mui/icons-material/ShieldOutlined";
import AssessmentIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import type { ReactNode } from "react";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getRuntimeConfig } from "../config/runtime-config";
import { useFinance } from "../pages/finance/FinanceContext";
import { FINANCE_NAV } from "../pages/finance/financeNav";

const config = getRuntimeConfig();

const NAV_ICONS: Record<string, ReactNode> = {
  dashboard: <DashboardIcon fontSize="small" />,
  summary: <SummarizeIcon fontSize="small" />,
  budget: <AccountBalanceWalletIcon fontSize="small" />,
  actuals: <ReceiptLongIcon fontSize="small" />,
  bva: <CompareArrowsIcon fontSize="small" />,
  monthly: <CalendarViewMonthIcon fontSize="small" />,
  forecast: <TimelineIcon fontSize="small" />,
  revenue: <TrendingUpIcon fontSize="small" />,
  cashflow: <WavesIcon fontSize="small" />,
  pnl: <FunctionsIcon fontSize="small" />,
  dept: <ApartmentIcon fontSize="small" />,
  cc: <GridViewIcon fontSize="small" />,
  upload: <UploadFileIcon fontSize="small" />,
  approvals: <FactCheckIcon fontSize="small" />,
  locking: <LockClockIcon fontSize="small" />,
  compare: <HistoryIcon fontSize="small" />,
  audit: <ChecklistIcon fontSize="small" />,
  users: <GroupIcon fontSize="small" />,
  roles: <ShieldIcon fontSize="small" />,
  reports: <AssessmentIcon fontSize="small" />,
  settings: <SettingsIcon fontSize="small" />,
};

/** Authenticated shell: Header + Sidebar (finance nav) + <Outlet /> for the matched child route. */
export function AppLayout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleMode } = useThemeMode();
  const { userName, userEmail, has } = useFinance();
  const [mobileOpen, setMobileOpen] = useState(false);

  const sidebarItems: SidebarItem[] = FINANCE_NAV.flatMap((group) =>
    group.items
      .filter((item) => has(item.perm))
      .map((item): SidebarItem => ({
        key: item.id,
        label: item.label,
        icon: NAV_ICONS[item.icon],
        group: group.group,
        selected:
          location.pathname === item.route ||
          (location.pathname === ROUTES.home && item.route === ROUTES.financeDashboard),
        onSelect: () => navigate(item.route),
      })),
  );

  async function handleLogout() {
    await dispatch(logout());
    navigate(ROUTES.login, { replace: true });
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Header
        appName={config.appName}
        userLabel={userName !== "—" ? userName : userEmail}
        themeMode={mode}
        onToggleTheme={toggleMode}
        onToggleSidebar={() => setMobileOpen((prev) => !prev)}
        onLogout={handleLogout}
      />
      <Sidebar
        items={sidebarItems}
        variant="permanent"
        open={mobileOpen || true}
        onClose={() => setMobileOpen(false)}
        width={260}
      />
      <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
